import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Save, ExternalLink, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { LOGO_URL } from "@/lib/company";

const input =
  "w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm text-[#0A0A0A] outline-none transition-colors focus:border-[#0A0A0A]";
const label = "mb-1 block text-xs font-medium uppercase tracking-wider text-[#757575]";

export default function Admin() {
  const { logout } = useAuth();
  const { refresh } = useContent();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    api.get("/content").then(({ data }) => setForm(data));
    api.get("/contact-submissions").then(({ data }) => setSubmissions(data)).catch(() => {});
  }, []);

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const setContact = (field, value) =>
    setForm((f) => ({ ...f, contact: { ...f.contact, [field]: value } }));
  const setService = (i, field, value) =>
    setForm((f) => ({ ...f, services: f.services.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)) }));
  const setTeam = (i, field, value) =>
    setForm((f) => ({ ...f, team: f.team.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        contact: { ...form.contact, lat: parseFloat(form.contact.lat), lng: parseFloat(form.contact.lng) },
        services: form.services.map((s) => ({
          ...s,
          points: (Array.isArray(s.points) ? s.points : String(s.points).split("\n")).map((p) => p.trim()).filter(Boolean),
        })),
      };
      await api.put("/content", payload);
      await refresh();
      toast.success("Änderungen gespeichert.");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F0F0F2]">
        <Loader2 className="animate-spin text-[#E8620E]" />
      </div>
    );
  }

  const Section = ({ title, children }) => (
    <section className="border border-[#E5E5E5] bg-white p-6 lg:p-8">
      <h2 className="text-lg font-semibold tracking-tight text-[#0A0A0A]">{title}</h2>
      <div className="mt-5">{children}</div>
    </section>
  );

  return (
    <div className="min-h-screen bg-[#F0F0F2]" data-testid="admin-page">
      {/* top bar */}
      <div className="sticky top-0 z-10 border-b border-[#E5E5E5] bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <img src={LOGO_URL} alt="Elvocom AG" className="h-8 w-auto object-contain" />
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" className="flex items-center gap-2 border border-[#E5E5E5] px-4 py-2 text-sm font-medium text-[#0A0A0A] transition-colors hover:border-[#0A0A0A]" data-testid="admin-view-site">
              <ExternalLink size={16} /> Website
            </Link>
            <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#E8620E] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#C4520C] disabled:opacity-60" data-testid="admin-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
            </button>
            <button onClick={onLogout} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#757575] transition-colors hover:text-[#0A0A0A]" data-testid="admin-logout">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl space-y-6 px-6 py-8">
        <p className="text-sm text-[#757575]">Hinweis: Bearbeitet wird der deutsche Inhalt. Kontaktdaten & Team erscheinen in allen Sprachen.</p>

        <Section title="Kontaktdaten & Standort">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className={label}>Strasse</label><input className={input} value={form.contact.street} onChange={(e) => setContact("street", e.target.value)} data-testid="admin-contact-street" /></div>
            <div><label className={label}>PLZ / Ort</label><input className={input} value={form.contact.city} onChange={(e) => setContact("city", e.target.value)} data-testid="admin-contact-city" /></div>
            <div><label className={label}>Telefon</label><input className={input} value={form.contact.phone} onChange={(e) => setContact("phone", e.target.value)} data-testid="admin-contact-phone" /></div>
            <div><label className={label}>E-Mail</label><input className={input} value={form.contact.email} onChange={(e) => setContact("email", e.target.value)} data-testid="admin-contact-email" /></div>
            <div><label className={label}>Website</label><input className={input} value={form.contact.website} onChange={(e) => setContact("website", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={label}>Karte Lat</label><input className={input} value={form.contact.lat} onChange={(e) => setContact("lat", e.target.value)} /></div>
              <div><label className={label}>Karte Lng</label><input className={input} value={form.contact.lng} onChange={(e) => setContact("lng", e.target.value)} /></div>
            </div>
          </div>
        </Section>

        <Section title="Leistungen">
          <div className="space-y-6">
            {form.services.map((s, i) => (
              <div key={s.key} className="border-t border-[#E5E5E5] pt-5 first:border-t-0 first:pt-0" data-testid={`admin-service-${i}`}>
                <div className="grid gap-4">
                  <div><label className={label}>Titel</label><input className={input} value={s.title} onChange={(e) => setService(i, "title", e.target.value)} data-testid={`admin-service-${i}-title`} /></div>
                  <div><label className={label}>Kurzbeschreibung</label><input className={input} value={s.short} onChange={(e) => setService(i, "short", e.target.value)} data-testid={`admin-service-${i}-short`} /></div>
                  <div><label className={label}>Stichpunkte (eine Zeile pro Punkt)</label>
                    <textarea className={`${input} resize-none`} rows={3}
                      value={Array.isArray(s.points) ? s.points.join("\n") : s.points}
                      onChange={(e) => setService(i, "points", e.target.value.split("\n"))}
                      data-testid={`admin-service-${i}-points`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Team">
          <div className="space-y-4">
            {form.team.map((m, i) => (
              <div key={i} className="grid gap-4 border-t border-[#E5E5E5] pt-4 first:border-t-0 first:pt-0 sm:grid-cols-2" data-testid={`admin-team-${i}`}>
                <div><label className={label}>Name</label><input className={input} value={m.name} onChange={(e) => setTeam(i, "name", e.target.value)} data-testid={`admin-team-${i}-name`} /></div>
                <div><label className={label}>Funktion</label><input className={input} value={m.role} onChange={(e) => setTeam(i, "role", e.target.value)} data-testid={`admin-team-${i}-role`} /></div>
              </div>
            ))}
          </div>
        </Section>

        <Section title={`Kontaktanfragen (${submissions.length})`}>
          {submissions.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-[#757575]"><Inbox size={16} /> Noch keine Anfragen.</div>
          ) : (
            <div className="space-y-4">
              {submissions.map((s) => (
                <div key={s.id} className="border border-[#E5E5E5] p-4 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-[#0A0A0A]">{s.name} · {s.email}</span>
                    <span className="text-xs text-[#757575]">{new Date(s.created_at).toLocaleString("de-CH")}</span>
                  </div>
                  <div className="mt-1 font-medium text-[#E8620E]">{s.subject}</div>
                  <p className="mt-1 whitespace-pre-wrap text-[#4A4A4A]">{s.message}</p>
                  {s.phone && <p className="mt-1 text-xs text-[#757575]">Tel: {s.phone}</p>}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
